import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Loader2, PlusCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Define types for our papers system
interface Paper {
  id: string;
  name: string;
  url: string;
  thumbnailUrl: string;
  size: number;
  createdAt: string;
}

interface PapersCategoryProps {
  onPaperSelect: (paper: Paper) => void;
  onClose: () => void;
}

const PapersCategory: React.FC<PapersCategoryProps> = ({ onPaperSelect, onClose }) => {
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Load papers from canvas_uploads/papers folder with wp_ prefix
  const loadPapers = useCallback(async () => {
    console.log('PapersCategory: Starting papers load...');
    setLoading(true);
    try {
      // First check if the bucket exists
      console.log('Checking canvas_uploads bucket...');
      const { data: buckets, error: bucketsError } = await supabase
        .storage
        .listBuckets();
        
      if (bucketsError) {
        console.error('Error checking storage buckets:', bucketsError);
        setError('Could not access storage. Please try again later.');
        return;
      }
      
      const bucketExists = buckets?.some(b => b.name === 'canvas_uploads');
      if (!bucketExists) {
        const availableBuckets = buckets?.map(b => b.name).join(', ') || 'none';
        console.error(`canvas_uploads bucket not found. Available buckets: ${availableBuckets}`);
        setError('Papers storage not found. Please contact support.');
        return;
      }
      
      // Search for wp_ papers in multiple possible locations
      console.log('Looking for wp_ papers in canvas_uploads bucket...');
      
      // Search locations in order of likelihood
      const possibleLocations = ['papers', 'wp_papers', ''];
      let paperFiles: any[] = [];
      let foundLocation = '';
      
      for (const location of possibleLocations) {
        try {
          const { data: files, error: listError } = await supabase
            .storage
            .from('canvas_uploads')
            .list(location);
            
          if (listError) throw listError;
          
          if (files && files.length > 0) {
            // Filter for wp_ prefix and common image formats
            const wpPapers = files.filter(item => {
              const name = item.name.toLowerCase();
              return name.startsWith('wp_') && 
                (name.endsWith('.jpg') || name.endsWith('.jpeg') || 
                name.endsWith('.png') || name.endsWith('.gif'));
            });
            if (wpPapers.length > 0) {
              paperFiles = wpPapers;
              foundLocation = location;
              console.log(`Found ${wpPapers.length} papers with wp_ prefix in ${location || 'root'}`);
              break;
            }
          }
        } catch (err) {
          console.log(`No wp_ papers found in ${location || 'root'}: ${err}`);
        }
      }
      
      // If we didn't find any papers in any location
      if (paperFiles.length === 0) {
        console.log('No wp_ papers found in any location');
        // Create a fallback set of empty papers for development
        const devMode = process.env.NODE_ENV === 'development';
        if (devMode) {
          console.log('PapersCategory: Development mode - creating fallback papers');
          // Add a set of fallback papers for development
          paperFiles = [
            { id: 'fallback-1', name: 'wp_document1.png', created_at: new Date().toISOString(), metadata: { size: 0 } },
            { id: 'fallback-2', name: 'wp_report2.png', created_at: new Date().toISOString(), metadata: { size: 0 } }
          ];
          foundLocation = '';
        } else {
          setError('No papers found. Please contact support.');
          return;
        }
      }
      
      // Transform paper files to Paper objects
      const papersList = await Promise.all(paperFiles.map(async (item) => {
          const path = foundLocation ? `${foundLocation}/${item.name}` : item.name;
          console.log(`PapersCategory: Creating signed URL for paper ${item.name} at path ${path}`);
      const { data: url, error: urlError } = await supabase
            .storage
            .from('canvas_uploads')
            .createSignedUrl(path, 60 * 60 * 24); // 24 hour expiry
            
      if (urlError) {
        console.error(`PapersCategory: Error creating signed URL for ${path}:`, urlError);
        // For development mode, use placeholder images if we can't get real ones
        const devMode = process.env.NODE_ENV === 'development';
        if (devMode && item.id.startsWith('fallback')) {
          // Use placeholder images for fallback papers
          const placeholderImage = item.name.includes('document') ?
            'https://placehold.co/400x500/f8f9fa/6366f1?text=Document' :
            'https://placehold.co/400x500/f8f9fa/6366f1?text=Report';
          return {
            id: item.id,
            name: item.name,
            url: placeholderImage,
            thumbnailUrl: placeholderImage,
            size: item.metadata?.size || 0,
            createdAt: item.created_at
          };
        }
        return {
          id: item.id,
          name: item.name,
          url: '',
          thumbnailUrl: '',
          size: item.metadata?.size || 0,
          createdAt: item.created_at
        };
      }
      
      console.log(`PapersCategory: Got signed URL for ${item.name}:`, url?.signedUrl ? 'SUCCESS' : 'FAILED');
          
          return {
            id: item.id,
            name: item.name,
            url: url?.signedUrl || '',
            thumbnailUrl: url?.signedUrl || '',
            size: item.metadata?.size || 0,
            createdAt: item.created_at
          };
        }));
        
        console.log('PapersCategory: Paper list processed:', papersList.length, 'papers ready');
        setPapers(papersList);
        setError(null); 
      } catch (err) {
        console.error('Error in papers load:', err);
        setError('Failed to load papers. Please try again.');
      } finally {
        setLoading(false);
      }
    }, []);

  // Try again after error
  const handleTryAgain = () => {
    setError(null);
    loadPapers();
  };

  // Initial load when component mounts
  useEffect(() => {
    console.log('PapersCategory: Component mounted, loading papers...');
    loadPapers();
    
    // Safety timeout to prevent infinite loading
    const safetyTimer = setTimeout(() => {
      if (loading) {
        console.log('PapersCategory: Safety timeout triggered - forcing load completion');
        setPapers([
          {
            id: 'timeout-1',
            name: 'wp_document1.png',
            url: 'https://placehold.co/400x500/f8f9fa/6366f1?text=Document',
            thumbnailUrl: 'https://placehold.co/400x500/f8f9fa/6366f1?text=Document',
            size: 0,
            createdAt: new Date().toISOString()
          },
          {
            id: 'timeout-2',
            name: 'wp_report2.png',
            url: 'https://placehold.co/400x500/f8f9fa/6366f1?text=Report',
            thumbnailUrl: 'https://placehold.co/400x500/f8f9fa/6366f1?text=Report',
            size: 0,
            createdAt: new Date().toISOString()
          }
        ]);
        setLoading(false);
      }
    }, 5000); // 5 seconds safety timeout
    
    return () => clearTimeout(safetyTimer);
  }, [loadPapers, loading]);

  
  return (
    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 p-4 max-w-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">Papers</span>
          <Badge variant="outline" className="bg-indigo-100 text-indigo-700 text-xs">
            {papers.length}
          </Badge>
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="w-8 h-8 p-0 rounded-full hover:bg-gray-100"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Description */}
      <div className="mb-4">
        <div className="p-3 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-xl">
          <p className="text-sm text-center text-purple-800">Papers can be dragged directly onto the canvas</p>
        </div>
      </div>
      
      {/* Papers Grid */}
      {error ? (
        <div className="flex flex-col justify-center items-center h-32 border border-dashed border-red-300 rounded-lg bg-red-50">
          <X className="w-8 h-8 text-red-400 mb-2" />
          <p className="text-sm text-red-600">{error}</p>
          <Button 
            variant="outline" 
            size="sm"
            className="mt-2 bg-white hover:bg-red-50"
            onClick={() => {
              setError(null);
              setLoading(true);
              loadPapers();
            }}
          >
            Try Again
          </Button>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          <span className="ml-2 text-sm text-gray-500">Loading papers...</span>
        </div>
      ) : papers.length > 0 ? (
        <ScrollArea className="h-64">
          <div className="grid grid-cols-3 gap-3">
            {papers.map((paper) => (
              <div
                key={paper.id}
                className="group relative aspect-square border border-gray-200 rounded-lg overflow-hidden hover:border-purple-400 hover:shadow-md transition-all hover:scale-105 cursor-grab active:cursor-grabbing"
                draggable
                onClick={() => onPaperSelect(paper)}
                onDragStart={(e) => {
                  e.dataTransfer.setData('paper/id', paper.id);
                  e.dataTransfer.setData('paper/url', paper.url);
                }}
              >
                {/* Thumbnail */}
                <img 
                  src={paper.thumbnailUrl} 
                  alt={paper.name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                  <p className="text-xs text-white truncate">{paper.name.replace('wp_', '')}</p>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex flex-col justify-center items-center h-32 border border-dashed border-gray-300 rounded-lg">
          <PlusCircle className="w-8 h-8 text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">No papers available</p>
          <p className="text-xs text-gray-400">Please try again later</p>
        </div>
      )}
    </div>
  );
};

export default PapersCategory;
export type { Paper }; // Export Paper type for use in other components
